/**
 * FERP Portal - AI-Assisted Intake Processing Hook (DRR-187)
 *
 * Sends pseudonymized text (from on-device OCR + Presidio NER) to the
 * intake agent, which classifies the document and returns entity proposals
 * for HITL confirmation.
 *
 * Pipeline: Phone OCR → Presidio NER (hub) → tokenized text → Claude → proposals
 *
 * The agent NEVER writes directly. All proposals require explicit user confirmation
 * before entity creation mutations are called.
 *
 * Usage:
 * ```tsx
 * const { process, proposals, loading, sessionMessages } = useIntakeProcess();
 * await process({
 *   prompt: 'Process this ID photo',
 *   mediaText: '<PERSON_1> born <DATE_OF_BIRTH_1> at <ADDRESS_1>',
 *   documentTypeHint: 'government_id',
 * });
 * // proposals = [{ proposalType: 'create_actor', target: 'actor', extractedFields: [...] }]
 * ```
 */

import { useState, useCallback, useRef } from 'react';
import { gql } from '@apollo/client/core';
import { useMutation } from '@apollo/client/react';

const INTAKE_PROCESS = gql`
  mutation IntakeProcess($input: IntakeProcessInput!) {
    intakeProcess(input: $input) {
      response
      documentType
      proposals {
        proposalType
        target
        extractedFields {
          fieldName
          value
          confidence
        }
        reason
        confidence
      }
      suggestedNextSteps
      sessionMessages
      usage {
        inputTokens
        outputTokens
      }
      processingTimeMs
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IntakeExtractedField {
  fieldName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  confidence: number;
}

export interface IntakeProposal {
  proposalType: 'create_actor' | 'create_fhir_patient' | 'launch_workflow' | string;
  target: string;
  extractedFields: IntakeExtractedField[];
  reason: string;
  confidence: number;
}

export interface IntakeStep {
  action: string;
  type?: string;
  entityId?: string;
  name?: string;
  timestamp: string;
}

interface IntakeProcessData {
  intakeProcess: {
    response: string;
    documentType: string | null;
    proposals: Array<{
      proposalType: string;
      target: string;
      extractedFields: Array<{
        fieldName: string;
        value: unknown;
        confidence: number;
      }>;
      reason: string;
      confidence: number;
    }>;
    suggestedNextSteps: string[];
    sessionMessages: unknown;
    usage: { inputTokens: number; outputTokens: number };
    processingTimeMs: number;
  };
}

export interface IntakeProcessInput {
  prompt: string;
  mediaText?: string;
  documentTypeHint?: string;
  intakeSessionId?: string;
  industry?: string;
}

export interface UseIntakeProcessReturn {
  /** Send pseudonymized text to the intake agent for classification + field extraction */
  process: (input: IntakeProcessInput) => Promise<IntakeProposal[]>;
  /** Agent's text response (explanation, guidance) */
  response: string | null;
  /** Classified document type */
  documentType: string | null;
  /** Proposed actions for HITL confirmation */
  proposals: IntakeProposal[];
  /** Suggested next steps from the agent */
  suggestedNextSteps: string[];
  /** Session messages for multi-turn continuity */
  sessionMessages: unknown;
  /** Completed intake steps in this session */
  steps: IntakeStep[];
  /** Whether processing is in progress */
  loading: boolean;
  /** Error message if processing failed */
  error: string | null;
  /** Processing time of last call (ms) */
  processingTimeMs: number | null;
  /** Record a completed step (after user confirms a proposal) */
  recordStep: (step: IntakeStep) => void;
  /** Reset the session */
  resetSession: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIntakeProcess(): UseIntakeProcessReturn {
  const [response, setResponse] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [proposals, setProposals] = useState<IntakeProposal[]>([]);
  const [suggestedNextSteps, setSuggestedNextSteps] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [processingTimeMs, setProcessingTimeMs] = useState<number | null>(null);
  const [steps, setSteps] = useState<IntakeStep[]>([]);

  // Persist session messages across turns (ref to avoid re-renders)
  const sessionMessagesRef = useRef<unknown>(null);

  const [mutate, { loading }] = useMutation<IntakeProcessData>(INTAKE_PROCESS);

  const process = useCallback(
    async (input: IntakeProcessInput): Promise<IntakeProposal[]> => {
      try {
        setError(null);
        setProposals([]);
        setResponse(null);
        setDocumentType(null);
        setSuggestedNextSteps([]);
        setProcessingTimeMs(null);

        const result = await mutate({
          variables: {
            input: {
              prompt: input.prompt,
              sessionMessages: sessionMessagesRef.current,
              mediaText: input.mediaText ?? null,
              documentTypeHint: input.documentTypeHint ?? null,
              intakeSessionId: input.intakeSessionId ?? null,
              industry: input.industry ?? 'healthcare',
            },
          },
        });

        const data = result.data?.intakeProcess;
        if (!data) {
          setError('No response from intake agent');
          return [];
        }

        // Persist session messages for next turn
        sessionMessagesRef.current = data.sessionMessages;

        setResponse(data.response);
        setDocumentType(data.documentType);
        setSuggestedNextSteps(data.suggestedNextSteps);
        setProcessingTimeMs(data.processingTimeMs);

        const mapped: IntakeProposal[] = data.proposals.map((p) => ({
          proposalType: p.proposalType,
          target: p.target,
          extractedFields: p.extractedFields.map((f) => ({
            fieldName: f.fieldName,
            value: f.value,
            confidence: f.confidence,
          })),
          reason: p.reason,
          confidence: p.confidence,
        }));

        setProposals(mapped);
        return mapped;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Intake processing failed';
        setError(msg);
        return [];
      }
    },
    [mutate],
  );

  const recordStep = useCallback((step: IntakeStep) => {
    setSteps((prev) => [...prev, step]);
  }, []);

  const resetSession = useCallback(() => {
    sessionMessagesRef.current = null;
    setResponse(null);
    setDocumentType(null);
    setProposals([]);
    setSuggestedNextSteps([]);
    setError(null);
    setProcessingTimeMs(null);
    setSteps([]);
  }, []);

  return {
    process,
    response,
    documentType,
    proposals,
    suggestedNextSteps,
    sessionMessages: sessionMessagesRef.current,
    steps,
    loading,
    error,
    processingTimeMs,
    recordStep,
    resetSession,
  };
}
